import { Avatar, Badge, Flex, Heading, Text, Dialog, Button } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import TableComponent from './TableComponent';
import SearchFilter from './SearchFilter';
import PaginationComponent from './PaginationComponent';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import axiosInstance from '../../config/axiosConfig';
import debounce from 'lodash/debounce';
import { highlightSearchTerm } from '../../utils/helpers';
import { createPortal } from 'react-dom';
import ViewUser from './ViewUser';

const ViewCaregivers = ({ department, triggerButton }) => {
  const [caregivers, setCaregivers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const pageSize = 8;

  const fetchCaregivers = useCallback(
    async (page = 1, query = searchQuery, activeOnly = showActiveOnly) => {
      try {
        const payload = {
          searchTerm: query.length >= 3 ? query : null,
          isActive: activeOnly ? true : null,
          pageNumber: page,
          pageSize: pageSize,
          roleId: 4, // Caregivers
          departmentId: department?.depId || null,
          departmentToken : department.token
        };
        const response = await axiosInstance.post('/user/get-caregivers', payload);
        if (response.data && response.data.result && response.data.result.userGetList) {
          const caregiversData = response.data.result.userGetList.map((item, index) => ({
            ...item,
            fullName: `${item.firstName} ${item.lastName}`,
          }));
          setCaregivers(caregiversData);
          setTotalCount(response.data.result.totalCount || caregiversData.length);
        } else {
          setCaregivers([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching caregivers:', error);
        setCaregivers([]);
        setTotalCount(0);
      }
    },
    [searchQuery, showActiveOnly, department]
  );

  const columns = [
    {
      title: 'User Name',
      key: 'userName',
      render: (value, row) => (
        <Flex gap="2" align="center">
          <Flex direction="column">
            <Heading size="1">{highlightSearchTerm(value, searchQuery)}</Heading>
          </Flex>
        </Flex>
      ),
    },
    {
      title: 'Name',
      key: 'fullName',
      render: (value, row) => (
        <Text size="2">
          {highlightSearchTerm(`${row.firstName} ${row.lastName}`, searchQuery)}
        </Text>
      ),
    },
    {
      title: 'Employee ID',
      key: 'employeeId',
      render: (value) => <Text size="2">{highlightSearchTerm(value || 'N/A', searchQuery)}</Text>,
    },
    {
      title: 'Status',
      key: 'isActive',
      render: (value) => (
        <Badge color={value ? 'green' : 'tomato'}>
          <MdOutlineVerifiedUser color={value ? 'green' : 'red'} />
          {value ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Flex gap="2" align="center">
          <ViewUser
            initialValues={row}
            onActionSuccess={() => fetchCaregivers(currentPage)}
          />
        </Flex>
      ),
    },
  ];

  const debouncedFetch = useCallback(
    debounce((page, query, activeOnly) => {
      fetchCaregivers(page, query, activeOnly);
    }, 300),
    [fetchCaregivers]
  );

  useEffect(() => {
    if (isOpen) {
      debouncedFetch(currentPage, searchQuery, showActiveOnly);
    }
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch, isOpen]);

  const handleSearchChange = (value) => {
    if (value.length === 0 || value.length >= 3) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  };

  const handleActiveToggle = () => {
    setShowActiveOnly(!showActiveOnly);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const dialogContent = isOpen ? (
    <Dialog.Content maxWidth="800px">
      <Dialog.Title>Caregivers of {department?.firstName || 'Department'}</Dialog.Title>
      <Dialog.Description size="2" mb="4">
        Browse and filter caregivers for {department?.firstName || 'this department'}.
      </Dialog.Description>
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" mb="4">
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly}
            placeholder="Search (min 3 chars)"
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
        </Flex>
        <TableComponent dataStructure={caregivers} columns={columns} />
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        )}
        {totalCount > 0 && (
          <Text size="2" align="center" mt="2">
            Showing {(currentPage - 1) * pageSize + 1} -{' '}
            {Math.min(currentPage * pageSize, totalCount)} of {totalCount} caregivers
          </Text>
        )}
      </Flex>
      <Flex gap="3" mt="4" justify="end">
        <Button variant="soft" color="gray" onClick={() => setIsOpen(false)}>
          Close
        </Button>
      </Flex>
    </Dialog.Content>
  ) : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger>
        {triggerButton}
      </Dialog.Trigger>
      {isOpen && createPortal(dialogContent, document.body)}
    </Dialog.Root>
  );
};

export default ViewCaregivers;
